import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADD_CONTACT,
  DELETE_CONTACT,
  GET_CONTACT,
  INVITE_CONTACT,
  REACTIVATE_CONTACT,
  UPDATE_CONTACT,
} from "../graphql/contact";
import ContactType from "../enums/ContactType";
import { useEffect, useState } from "react";
import {
  generateBusinessContactValidationSchema,
  generateIndividualContactValidationSchema,
} from "../validations/addContact";
import RelationshipType from "../enums/RelationshipType";
import {
  LocalImageAsset,
  useAttachmentAndAssetHandling,
} from "./useAttachmentAndAssetHandling";
import { ATTACHMENT_PARENT_TYPE } from "enums/AttachmentParentType";
import useCustomFieldRenderer from "./useCustomFieldRenderer";
import CustomFieldParentType from "enums/CustomFieldParentType";
import { CustomFieldsRecord } from "hooks/customFields.types";

export type FormikContact = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  website: string;
  title: string;
  parentContactId?: string;
  businessName: string;
  contactType: ContactType | null;
  relationshipType?: RelationshipType | null;
  invoices?: string[];
  attachments?: LocalImageAsset[];
  address: {
    addressName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
  };
  showPaymentDetails: boolean;
  paymentDetails: {
    accountNumber: string;
    routingNumber: string;
    accountName: string;
  };
  paymentBankInfo: string;
  removeBank: boolean;
  status?: string;
  inviteContact?: boolean;
  customFields?: CustomFieldsRecord;
};

/** A contact address with every part empty, which saving leaves off the contact. */
export const EMPTY_CONTACT_ADDRESS: FormikContact["address"] = {
  addressName: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  zipcode: "",
  country: "",
};

type UseContactDataParams = {
  contactId?: string;
  shouldNotQueryContact?: boolean;
};

function isObjectEmpty(obj: Record<string, any>) {
  if (!obj) return true;
  return Object.values(obj).every((v) => v === "" || v == null);
}

const submitHelper = (
  values: FormikContact,
  isCreate: boolean,
  transformCustomFieldsToArray: (
    customFields: CustomFieldsRecord | undefined,
  ) => any,
) => {
  const commonInput = {
    email: values.email,
    phone: values.phone,
    contactType: values.contactType,
    relationshipType: values.relationshipType,
    paymentDetails: {
      accountNumber: values.paymentDetails.accountNumber,
      routingNumber: values.paymentDetails.routingNumber,
      accountName: values.paymentDetails.accountName,
    },
    customFields: transformCustomFieldsToArray(values?.customFields),
  };

  if (!isObjectEmpty(values.address)) {
    (commonInput as any).address = values.address;
  }

  if (!isObjectEmpty(values.paymentDetails)) {
    (commonInput as any).paymentDetails = values.paymentDetails;
  }

  if (isCreate && values.inviteContact) {
    (commonInput as any).inviteContact = values.inviteContact;
  }

  if (!isCreate) {
    (commonInput as any).removeBank = values.removeBank;
  }

  const individualInput = {
    ...commonInput,
    parentContactId: values.parentContactId,
    firstName: values.firstName,
    lastName: values.lastName,
    title: values.title,
  };

  const businessInput = {
    ...commonInput,
    businessName: values.businessName,
    website: values.website,
  };

  const input =
    values.contactType === ContactType.Individual
      ? individualInput
      : businessInput;

  return input;
};

const useContactData = ({
  contactId = "",
  shouldNotQueryContact = false,
}: UseContactDataParams) => {
  const [validationSchema, setValidationSchema] = useState<any>(null);

  const {
    customFieldSettings,
    transformCustomFieldsToRecord,
    transformCustomFieldsToArray,
  } = useCustomFieldRenderer({
    parentType: CustomFieldParentType.CONTACT,
  });

  const getValidationSchema = (contactType: ContactType) => {
    return contactType === ContactType.Individual
      ? generateIndividualContactValidationSchema(customFieldSettings)
      : generateBusinessContactValidationSchema(customFieldSettings);
  };

  const { handleAddAttachments } = useAttachmentAndAssetHandling({});

  const [addNewContact, { loading: creatingContact }] =
    useMutation(ADD_CONTACT);
  const [updateContact, { loading: updatingContact }] =
    useMutation(UPDATE_CONTACT);
  const [removeContact, { loading: deletingContact }] =
    useMutation(DELETE_CONTACT);
  const [reactivateContact, { loading: reactivatingContact }] =
    useMutation(REACTIVATE_CONTACT);
  const [inviteContact, { loading: invitingContact }] =
    useMutation(INVITE_CONTACT);

  const {
    data,
    loading: loadingContact,
    refetch: refetchContact,
  } = useQuery(GET_CONTACT, {
    variables: { id: contactId },
    fetchPolicy: "cache-and-network",
    skip: !contactId || shouldNotQueryContact,
  });

  useEffect(() => {
    if (data?.contact) {
      const contactType = data.contact.contactType as ContactType;
      const newValidationSchema = getValidationSchema(contactType);

      setValidationSchema(newValidationSchema);

      const populatedValues = {
        id: data.contact.id,
        firstName: data.contact.firstName,
        lastName: data.contact.lastName ?? "",
        email: data.contact.email,
        phone: data.contact.phone,
        website: data.contact.website,
        title: data.contact.title ?? "",
        parentContactId: data.contact.parentContactId,
        businessName: data.contact.businessName,
        contactType: data.contact.contactType as ContactType,
        relationshipType: data.contact.relationshipType as RelationshipType,
        invoices: data.contact.invoices,
        attachments: data.contact.attachments,
        address: {
          addressName: data.contact.address?.addressName,
          address1: data.contact.address?.address1,
          address2: data.contact.address?.address2,
          city: data.contact.address?.city,
          state: data.contact.address?.state,
          zipcode: data.contact.address?.zipcode,
          country: data.contact.address?.country,
        },
        showPaymentDetails: false,
        paymentDetails: {
          accountNumber: "",
          routingNumber: "",
          accountName: "",
        },
        paymentBankInfo: data.contact.paymentBankInfo,
        removeBank: false,
        status: data.contact.status,
        inviteContact: false,
        customFields: transformCustomFieldsToRecord(
          data?.contact?.customFields,
          customFieldSettings,
        ),
      };

      formikContact.resetForm({ values: populatedValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.contact]);

  const formikContact = useFormik<FormikContact>({
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      website: "",
      title: "",
      parentContactId: "",
      businessName: "",
      contactType: null,
      relationshipType: null,
      invoices: [],
      attachments: [],
      address: EMPTY_CONTACT_ADDRESS,
      showPaymentDetails: false,
      paymentDetails: {
        accountNumber: "",
        routingNumber: "",
        accountName: "",
      },
      paymentBankInfo: "",
      removeBank: false,
      status: "",
      inviteContact: false,
      customFields: {},
    },
    validationSchema: validationSchema,
    onSubmit: () => {},
  });

  // handles dynamic switching of individual vs business contact validations
  useEffect(() => {
    const currentContactType = formikContact.values.contactType;
    if (currentContactType) {
      const newValidationSchema = getValidationSchema(currentContactType);
      if (!contactId) {
        formikContact.resetForm({
          values: {
            ...formikContact.initialValues,
            contactType: currentContactType,
            parentContactId:
              currentContactType === ContactType.Business
                ? ""
                : formikContact.values.parentContactId || "",
            customFields: formikContact.values.customFields,
          },
        });
      }
      setValidationSchema(newValidationSchema);
      formikContact.setFieldValue("contactType", currentContactType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikContact.values.contactType]);

  async function handleCreateContact(values: FormikContact) {
    const input = submitHelper(values, true, transformCustomFieldsToArray);
    const result = await addNewContact({ variables: { input } });

    if (values?.attachments && values.attachments?.length > 0) {
      await handleAddAttachments(values.attachments, {
        parentId: result.data.addContact.id,
        parentType: ATTACHMENT_PARENT_TYPE.CONTACT,
      });
    }
    return result;
  }

  async function handleUpdateContact(values: FormikContact) {
    if (!contactId) return null;
    const input = submitHelper(values, false, transformCustomFieldsToArray);
    const result = await updateContact({ variables: { id: contactId, input } });
    return result;
  }

  const handleRemoveContact = async (id?: string) => {
    const contactToRemove = id || contactId;
    if (!contactToRemove) return null;
    const result = await removeContact({ variables: { id: contactToRemove } });
    return result;
  };

  const handleReactivateContact = async (id?: string) => {
    const contactToReactivate = id || contactId;
    if (!contactToReactivate) return null;
    const result = await reactivateContact({
      variables: { id: contactToReactivate },
    });
    return result;
  };

  const handleInviteContact = async (contactId: string | null) => {
    const result = await inviteContact({ variables: { id: contactId } });
    return result;
  };

  const specificLoadingStates: ContactLoadingStates = {
    creatingContact,
    updatingContact,
    deletingContact,
    loadingContact,
    reactivatingContact,
    invitingContact,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    formikContact,
    customFieldSettings,
    specificLoadingStates,
    isAnyLoading,
    refetchContact,
    handleCreateContact,
    handleUpdateContact,
    handleRemoveContact,
    handleReactivateContact,
    handleInviteContact,
  };
};

export default useContactData;

export interface ContactLoadingStates {
  creatingContact: boolean;
  updatingContact: boolean;
  deletingContact: boolean;
  loadingContact: boolean;
  reactivatingContact: boolean;
  invitingContact: boolean;
}
